/**
 * Adding a session to an activity (D19).
 *
 * The authoring step that unblocks assignment: a volunteer is assigned to a
 * SESSION, and an activity created in the portal has none until one is added
 * here. A session is one occurrence of the activity, serves at least one
 * beneficiary community — so the
 * moment it is saved, the roster below can take volunteers.
 *
 * Most activities are a single session, so the form leads
 * with that shape (names pre-filled from the activity) and asks only for the
 * communities and dates that are genuinely per-session.
 */

import { useState } from 'react';
import { ACTIVITY_STATUS, SESSION_RESPONSIBILITY } from '@parinaam-vms/shared';
import type { ActivityWithProgram } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  CheckboxGroup,
  DurationInput,
  LoadingState,
  TextArea,
  TextInput,
} from '../../components/common';
import type { SelectOption } from '../../components/common';
import { useToast } from '../../components/common/Toast';
import { useCommunities } from '../../hooks/useCommunities';
import { useCreateSession } from '../../hooks/useSessions';
import styles from './ActivitiesPage.module.css';

/**
 * Who signs a new session off — Parinaam, and the form no longer asks.
 *
 * The question was on the form until 8 Oct 2026, offering Parinaam, the
 * volunteer or both. Parinaam's instruction is that their own confirmation is
 * what completes a session and a volunteer's sign-off is not required, so the
 * answer is the same every time and asking it was a decision staff had to make
 * on every session without anything to base it on.
 *
 * This is a default, not a removal. `responsibility` still drives who may
 * confirm — the session detail page reads it, and so does the volunteer's own
 * session list, where a PARINAAM session correctly shows no Confirm button. A
 * session needing volunteer sign-off can still be made through the API, and
 * the ones already recorded that way keep working.
 */
const SIGNED_OFF_BY = SESSION_RESPONSIBILITY.PARINAAM;

export function AddSessionPanel({ activity }: { activity: ActivityWithProgram }) {
  const [adding, setAdding] = useState(false);

  // Nothing is added under a closed activity — the roster and its attendance
  // would be invisible to the dashboard. The API refuses it too (§4.22 spirit).
  if (
    activity.status === ACTIVITY_STATUS.CANCELLED ||
    activity.status === ACTIVITY_STATUS.DELETED
  ) {
    return null;
  }

  return (
    <Card
      title="Add a session"
      action={
        !adding && (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add a session
          </Button>
        )
      }
    >
      {adding ? (
        <SessionForm activity={activity} onClose={() => setAdding(false)} />
      ) : (
        <p className={styles.sessionHint}>
          Add a session to open this activity.
        </p>
      )}
    </Card>
  );
}

function SessionForm({
  activity,
  onClose,
}: {
  activity: ActivityWithProgram;
  onClose: () => void;
}) {
  const toast = useToast();
  const communities = useCommunities();
  const create = useCreateSession(activity.activity_id);

  // Pre-filled from the activity, which is the common case: one activity, one
  // session. Staff change the name only when it is genuinely one of several.
  const [name, setName] = useState(activity.name);
  const [communityIds, setCommunityIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(activity.start_date);
  const [endDate, setEndDate] = useState('');
  // The activity's usual timing and place, as a starting point.
  const [startTime, setStartTime] = useState(activity.start_time?.slice(0, 5) ?? '');
  const [endTime, setEndTime] = useState(activity.end_time?.slice(0, 5) ?? '');
  const [location, setLocation] = useState(activity.location ?? '');
  const [description, setDescription] = useState('');
  const [plannedMinutes, setPlannedMinutes] = useState(0);
  const [volunteersNeeded, setVolunteersNeeded] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const communityOptions: SelectOption[] = (communities.data ?? []).map((community) => ({
    value: community.community_id,
    label: community.city ? `${community.name} — ${community.city}` : community.name,
  }));
  const noCommunities = communities.isSuccess && communityOptions.length === 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems: Record<string, string> = {};
    if (name.trim() === '') problems.name = 'Give the session a name.';
    if (communityIds.length === 0) {
      problems.community_ids = 'Choose at least one community this session serves.';
    }
    if (startDate === '') problems.start_date = 'When does this session start?';
    if (endDate && startDate && endDate < startDate) {
      problems.end_date = 'The end date must be on or after the start date.';
    }
    if (startTime && endTime && endTime <= startTime) {
      problems.end_time = 'The end time must be after the start time.';
    }
    if (volunteersNeeded && (!/^\d+$/.test(volunteersNeeded) || Number(volunteersNeeded) < 1)) {
      problems.volunteers_needed = 'Enter a whole number of at least 1, or leave it blank.';
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    create.mutate(
      {
        name: name.trim(),
        community_ids: communityIds,
        start_date: startDate,
        end_date: endDate || null,
        start_time: startTime || null,
        end_time: endTime || null,
        description: description.trim() || null,
        location: location.trim() || null,
        planned_minutes: plannedMinutes,
        responsibility: SIGNED_OFF_BY,
        volunteers_needed: volunteersNeeded ? Number(volunteersNeeded) : null,
      },
      {
        onSuccess: () => {
          toast.success('Session added. You can now assign volunteers to it.');
          onClose();
        },
        onError: (error) => toast.error(error, 'Could not add the session.'),
      }
    );
  };

  if (communities.isPending) return <LoadingState label="Loading communities…" />;

  return (
    <form onSubmit={handleSubmit} noValidate>
      {create.isError && (
        <p className={styles.error} role="alert">
          {(create.error as Error).message}
        </p>
      )}

      <fieldset className={styles.section} disabled={create.isPending}>
        <TextInput
          label="Session name"
          required
          value={name}
          error={errors.name}
          onChange={(event) => setName(event.target.value)}
        />

        <TextArea
          label="Session description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />

        {noCommunities ? (
          <p className={styles.error} role="alert">
            No communities yet — add one first.
          </p>
        ) : (
          <CheckboxGroup
            label="Which communities does this session serve?"
            options={communityOptions}
            value={communityIds}
            error={errors.community_ids}
            onChange={setCommunityIds}
          />
        )}

        <div className={styles.row}>
          <TextInput
            label="Session start date"
            type="date"
            required
            value={startDate}
            error={errors.start_date}
            onChange={(event) => setStartDate(event.target.value)}
          />
          <TextInput
            label="Session end date"
            type="date"
            value={endDate}
            error={errors.end_date}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </div>

        <div className={styles.row}>
          <TextInput
            label="Session start time"
            type="time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
          />
          <TextInput
            label="Session end time"
            type="time"
            value={endTime}
            error={errors.end_time}
            onChange={(event) => setEndTime(event.target.value)}
          />
        </div>

        <TextInput
          label="Session location"
          value={location}
          onChange={(event) => setLocation(event.target.value)}
        />

        <DurationInput
          label="Number of hours per session"
          value={plannedMinutes}
          onChange={setPlannedMinutes}
        />

        <TextInput
          label="Number of volunteers per session"
          type="number"
          min={1}
          placeholder="Leave blank for no limit"
          value={volunteersNeeded}
          error={errors.volunteers_needed}
          onChange={(event) => setVolunteersNeeded(event.target.value)}
        />
      </fieldset>

      <div className={styles.actions}>
        <Button type="submit" variant="primary" loading={create.isPending} disabled={noCommunities}>
          Add session
        </Button>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
